import { supabase } from '@/integrations/supabase/client';

const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  '840496747404-hmhfjio8gr80ve9cu4621c2sqil55cu5.apps.googleusercontent.com';

export const REDIRECT_URI =
  typeof window !== 'undefined'
    ? `${window.location.origin}/oauth/google/callback`
    : 'http://localhost:5173/oauth/google/callback';
export const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/drive.file';


/**
 * Check if the current user already has a stored Google OAuth token in Supabase.
 */
export async function hasGoogleToken(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase
    .from('google_oauth_tokens')
    .select('user_id')
    .eq('user_id', user.id)
    .maybeSingle();

  return !error && !!data;
}

/**
 * Builds the Google OAuth2 authorization URL.
 */
export function buildGoogleAuthUrl(): string {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: GOOGLE_SCOPES,
    access_type: 'offline',
    prompt: 'consent',
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Opens the Google OAuth consent screen in a popup window.
 * Resolves when the user authorises, or rejects on closure/error.
 */
export function openGoogleAuthPopup(): Promise<void> {
  return new Promise((resolve, reject) => {
    const authUrl = buildGoogleAuthUrl();

    const popup = window.open(
      authUrl,
      'google-oauth',
      'width=500,height=650,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      reject(new Error('Popup was blocked. Please allow popups for this site and try again.'));
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data === 'google-auth-success') {
        cleanup();
        resolve();
      } else if (event.data?.type === 'google-auth-error') {
        cleanup();
        reject(new Error(event.data.message || 'Google authentication failed'));
      }
    };

    const pollClosed = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error('Authentication cancelled — popup was closed.'));
      }
    }, 500);

    function cleanup() {
      window.removeEventListener('message', onMessage);
      clearInterval(pollClosed);
      if (!popup.closed) popup.close();
    }

    window.addEventListener('message', onMessage);
  });
}
